import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { fetchSpecies, type SpeciesOption } from "@/src/api/species";

export type SpeciesSelection = {
  speciesId: string;
};

type SpeciesStepProps = {
  onNext: (selection: SpeciesSelection) => void;
};

export function SpeciesStep({ onNext }: SpeciesStepProps) {
  const [options, setOptions] = useState<SpeciesOption[] | null>(null);
  const [selected, setSelected] = useState<SpeciesOption | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    fetchSpecies()
      .then((data) => {
        if (active) {
          setOptions(data);
        }
      })
      .catch((e: unknown) => {
        if (active) {
          setError(e instanceof Error ? e.message : String(e));
        }
      });
    return () => {
      active = false;
    };
  }, []);

  if (error) {
    return <div className="p-4 text-red-500">Error: {error}</div>;
  }
  if (!options) {
    return <div className="p-4">Loading species...</div>;
  }

  const canContinue = selected !== null;

  return (
    <div className="mx-auto mt-8 w-full max-w-2xl space-y-8 px-4">
      <div className="space-y-2 text-center">
        <h1 className="font-bold text-3xl tracking-tight">
          Choose your Lineage
        </h1>
        <p className="text-muted-foreground">Select a species to begin.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {options.map((opt) => (
          <Card
            className={`cursor-pointer transition-all hover:border-primary ${
              selected?.speciesId === opt.speciesId
                ? "border-primary ring-2 ring-primary ring-offset-2"
                : "border-muted"
            }`}
            key={opt.speciesId}
            onClick={() => {
              setSelected(opt);
            }}
          >
            <CardHeader className="space-y-3">
              <CardTitle>{opt.label}</CardTitle>
              {opt.baseWalkSpeed !== undefined && (
                <p className="text-muted-foreground text-sm">
                  {opt.baseWalkSpeed} ft speed
                </p>
              )}
              {opt.traits?.length ? (
                <div className="space-y-2">
                  {opt.traits.slice(0, 4).map((trait) => (
                    <div className="space-y-0.5" key={trait.label}>
                      <p className="font-medium text-sm">{trait.label}</p>
                      <p className="text-muted-foreground text-xs leading-relaxed">
                        {trait.description}
                      </p>
                    </div>
                  ))}
                </div>
              ) : null}
            </CardHeader>
          </Card>
        ))}
      </div>

      <div className="flex justify-end pt-8 pb-12">
        <Button
          disabled={!canContinue}
          onClick={() => {
            if (!selected) {
              return;
            }
            onNext({
              speciesId: selected.speciesId,
            });
          }}
          size="lg"
          type="button"
        >
          Next
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
